import React, { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import {
	useAgentStore,
	initializeAgentStore,
	OrganizationAgent
} from '@/pages/CustomerSupport/useAgents';
import { Typography } from '@/stories/Library/Components/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import { Circle, Power } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { getLanguageFromPath } from '@/utils/utils';
import { Outlet } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { Cpu } from 'react-feather';
import { Roles } from '@/pages/ClientProfiles/Roles.types';
import { useSupabaseAuth } from '@/pages/CustomerSupport/useSupabaseAuth';

const ManageAgentsPage: React.FC = () => {
	const t = useTranslations('ManageAgents');
	const language = getLanguageFromPath(window.location.pathname);
	const navigate = useNavigate();
	const { isLoading: authLoading } = useSupabaseAuth(Roles.KOGNI_ADMIN);
	const { agents, isLoading, error, toggleAgentActive, setSelectedAgent } = useAgentStore();
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [agentToToggle, setAgentToToggle] = useState<{ id: string; currentStatus: boolean } | null>(
		null
	);
	const { agentId } = useParams();

	useEffect(() => {
		if (!authLoading) {
			initializeAgentStore();
		}
	}, [authLoading]);

	useEffect(() => {
		if (agentId && agents.length > 0) {
			const agent = agents.find((a) => a.id === agentId);
			setSelectedAgent(agent ?? null);
		} else if (!agentId) {
			setSelectedAgent(null);
		}
	}, [agentId, agents, setSelectedAgent]);

	const handleViewDetails = (agent: OrganizationAgent) => {
		navigate(`/${language}/center/manage-agents/agents/${agent.id}`);
		setSelectedAgent(agent);
	};

	const handleToggleClick = (agentId: string, currentStatus: boolean) => {
		setAgentToToggle({ id: agentId, currentStatus });
		setShowConfirmModal(true);
	};

	const handleConfirmToggle = async () => {
		if (agentToToggle) {
			await toggleAgentActive(agentToToggle.id, !agentToToggle.currentStatus);
		}
		setShowConfirmModal(false);
		setAgentToToggle(null);
	};

	const handleCancelToggle = () => {
		setShowConfirmModal(false);
		setAgentToToggle(null);
	};

	if (authLoading || isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Typography size="lg" weight="medium">
					{t('LoadingAgents')}
				</Typography>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Typography size="lg" weight="medium" color="error">
					{error}
				</Typography>
			</div>
		);
	}

	if (agentId) {
		return <Outlet />;
	}

	return (
		<div className="container mx-auto p-8">
			<div className="mb-6 flex flex-col gap-6">
				<Typography type="display" size="md">
					{t('Title')}
				</Typography>
				<Typography size="lg" color="secondary" weight="regular">
					{t('Description')}
				</Typography>
			</div>

			{agents.length === 0 ? (
				<div className="flex items-center justify-center py-12">
					<div className="text-center flex flex-col gap-2">
						<Typography size="lg" color="secondary" weight="medium">
							{t('NoAgentsFound')}
						</Typography>
						<Typography size="sm" color="secondary" weight="regular">
							{t('CreateFirstAgent')}
						</Typography>
					</div>
				</div>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
					{agents.map((agent) => (
						<div
							key={agent.id}
							className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200"
						>
							<div className="flex items-start justify-between mb-4">
								<div className="flex-1 flex flex-col gap-4">
									<Typography size="xl" weight="semibold">
										{agent.agent_type}
									</Typography>
									<div className="flex items-center gap-2">
										<Cpu className="w-4 h-4" />
										<Typography size="sm" color="secondary" weight="medium">
											{agent.model.toUpperCase()}
										</Typography>
									</div>
								</div>
								<div title={t('ToggleStatus')}>
									<Button
										onClick={() => handleToggleClick(agent.id, agent.is_active)}
										color={agent.is_active ? 'success' : 'gray'}
										size="small"
										startIcon={
											agent.is_active ? (
												<Circle className="w-4 h-4" />
											) : (
												<Power className="w-4 h-4" />
											)
										}
										label={agent.is_active ? t('Active') : t('Inactive')}
										outlined
									/>
								</div>
							</div>

							<div className="space-y-3">
								<div className="pt-3 flex items-center justify-between">
									<Typography size="xs" color="secondary" weight="regular">
										{t('Created')}: {new Date(agent.created_at).toLocaleDateString()}
									</Typography>
									<Button
										onClick={() => handleViewDetails(agent)}
										color="primary"
										size="small"
										textOnly
										label={t('ViewDetails')}
									/>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			<ConfirmModal
				isOpen={showConfirmModal}
				title={t('ConfirmToggleTitle')}
				subtitle={
					agentToToggle?.currentStatus
						? t('ConfirmDeactivateSubtitle')
						: t('ConfirmActivateSubtitle')
				}
				confirmActionText={t('Confirm')}
				onConfirm={handleConfirmToggle}
				onCancel={handleCancelToggle}
				onClose={handleCancelToggle}
				type="confirm"
			/>
		</div>
	);
};

export default ManageAgentsPage;
